/**
 * Corner minimap with fog of war. Cells are revealed as the player walks near
 * them; unrevealed cells stay dark. Draws walkable floor, blocked/boundary,
 * the player, and the exit (once discovered).
 */
export class Minimap {
  constructor() {
    this.canvas = document.getElementById("minimap");
    if (this.canvas) { this.canvas.width = 176; this.canvas.height = 176; this.ctx = this.canvas.getContext("2d"); }
    this.visited = null;
  }

  reset(level) {
    this.level = level;
    this.visited = Array.from({ length: level.gridH }, () => new Uint8Array(level.gridW));
  }

  /** Compact the explored fog-of-war grid for saving. */
  serialize() {
    if (!this.visited) return null;
    const h = this.visited.length, w = h ? this.visited[0].length : 0;
    let bits = "";
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) bits += this.visited[y][x] ? "1" : "0";
    return { w, h, bits };
  }
  /** Restore a saved fog-of-war grid. */
  restore(data) {
    if (!data || !this.visited || !data.bits) return;
    const { w, h, bits } = data;
    for (let y = 0; y < h && y < this.visited.length; y++)
      for (let x = 0; x < w && x < this.visited[y].length; x++)
        this.visited[y][x] = bits[y * w + x] === "1" ? 1 : 0;
  }

  update(level, player, portal, markers = null) {
    if (!this.ctx || !this.visited) return;
    const g = level.worldToGrid(player.pos.x, player.pos.z);
    const px = Math.floor(g.gx), py = Math.floor(g.gy);
    const R = 5;
    for (let dy = -R; dy <= R; dy++)
      for (let dx = -R; dx <= R; dx++) {
        if (dx * dx + dy * dy > R * R) continue;
        const x = px + dx, y = py + dy;
        if (x >= 0 && y >= 0 && x < level.gridW && y < level.gridH) this.visited[y][x] = 1;
      }

    // once the floor is cleared, the portal's surroundings are revealed on the map
    if (portal) {
      const pe = level.worldToGrid(portal.pos.x, portal.pos.z);
      const ex0 = Math.floor(pe.gx), ey0 = Math.floor(pe.gy), PR = 3;
      for (let dy = -PR; dy <= PR; dy++)
        for (let dx = -PR; dx <= PR; dx++) {
          if (dx * dx + dy * dy > PR * PR) continue;
          const x = ex0 + dx, y = ey0 + dy;
          if (x >= 0 && y >= 0 && x < level.gridW && y < level.gridH) this.visited[y][x] = 1;
        }
    }

    const ctx = this.ctx, W = this.canvas.width, H = this.canvas.height;
    ctx.clearRect(0, 0, W, H);
    const cell = Math.min(W / level.gridW, H / level.gridH);
    const ox = (W - cell * level.gridW) / 2, oy = (H - cell * level.gridH) / 2;

    for (let y = 0; y < level.gridH; y++)
      for (let x = 0; x < level.gridW; x++) {
        if (!this.visited[y][x]) continue;
        let col = null;
        if (level.floor[y][x] === 1) col = level.block[y][x] === 0 ? "#4a5568" : "#5a4636";
        else if (level.isBoundary(x, y)) col = "#141821";
        if (col) { ctx.fillStyle = col; ctx.fillRect(ox + x * cell, oy + y * cell, cell + 0.6, cell + 0.6); }
      }

    // ---- key-point markers (hub stations / helix), drawn regardless of fog ----
    if (markers) {
      for (const m of markers) {
        const e = level.worldToGrid(m.x, m.z);
        const mx = ox + e.gx * cell, my = oy + e.gy * cell, r = Math.max(3, cell * 0.9);
        ctx.beginPath(); ctx.arc(mx, my, r, 0, Math.PI * 2);
        ctx.fillStyle = m.color || "#ffffff"; ctx.fill();
        ctx.lineWidth = 1.5; ctx.strokeStyle = "rgba(0,0,0,0.55)"; ctx.stroke();
      }
    }

    // ---- exit / descent portal: a bright diamond once discovered ----
    if (portal) {
      const e = level.worldToGrid(portal.pos.x, portal.pos.z);
      const ex = Math.floor(e.gx), ey = Math.floor(e.gy);
      if (this.visited[ey] && this.visited[ey][ex]) {
        const cx = ox + e.gx * cell, cy = oy + e.gy * cell, r = Math.max(3.5, cell * 1.1);
        ctx.beginPath();
        ctx.moveTo(cx, cy - r); ctx.lineTo(cx + r, cy); ctx.lineTo(cx, cy + r); ctx.lineTo(cx - r, cy); ctx.closePath();
        ctx.fillStyle = "#ffd24a"; ctx.fill();
        ctx.lineWidth = 1.5; ctx.strokeStyle = "#7a5a10"; ctx.stroke();
      }
    }

    ctx.fillStyle = "#5fd35f";
    ctx.beginPath();
    ctx.arc(ox + g.gx * cell, oy + g.gy * cell, Math.max(2.2, cell * 0.7), 0, Math.PI * 2);
    ctx.fill();
    // facing tick
    ctx.strokeStyle = "#5fd35f"; ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(ox + g.gx * cell, oy + g.gy * cell);
    ctx.lineTo(ox + g.gx * cell + Math.sin(player.yaw) * cell * 1.6, oy + g.gy * cell + Math.cos(player.yaw) * cell * 1.6);
    ctx.stroke();
  }
}
